import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { classifyStudent, RetentionCategory } from '@/lib/retention';

export interface WeeklyTrend {
  weekStart: string;            // ISO date (segunda-feira)
  counts: Record<RetentionCategory, number>;
}

/**
 * Histórico semanal de distribuição de categorias. Útil pro gráfico de
 * tendência. Pra cada semana, classifica os alunos como estariam no fim
 * daquela semana.
 */
export function useRetentionTrend(personalId: string, weeks: number = 8) {
  return useQuery({
    queryKey: ['retention-trend', personalId, weeks],
    queryFn: async (): Promise<WeeklyTrend[]> => {
      const { data: students } = await supabase
        .from('students')
        .select('id')
        .eq('personal_id', personalId)
        .eq('is_self', false)
        .eq('status', 'active');
      const studentIds = (students ?? []).map((s: { id: string }) => s.id);
      if (studentIds.length === 0) return [];

      // Busca sessions desde N+4 semanas atrás (folga pra cálculo do mais antigo)
      const since = new Date(Date.now() - (weeks + 4) * 7 * 86_400_000).toISOString();
      const { data: sessions } = await supabase
        .from('workout_sessions')
        .select('student_id, completed_at')
        .in('student_id', studentIds)
        .gte('completed_at', since)
        .not('completed_at', 'is', null);

      const sessionsByStudent: Record<string, { completed_at: string }[]> = {};
      for (const s of (sessions ?? []) as { student_id: string; completed_at: string }[]) {
        (sessionsByStudent[s.student_id] ||= []).push({ completed_at: s.completed_at });
      }

      // Encontra a última segunda-feira (semana atual)
      const now = new Date();
      const lastMonday = new Date(now);
      const day = lastMonday.getDay(); // 0=domingo, 1=segunda, ..., 6=sábado
      const diff = day === 0 ? -6 : 1 - day;
      lastMonday.setDate(lastMonday.getDate() + diff);
      lastMonday.setHours(0, 0, 0, 0);

      const trends: WeeklyTrend[] = [];
      for (let i = weeks - 1; i >= 0; i--) {
        const weekStart = new Date(lastMonday);
        weekStart.setDate(weekStart.getDate() - i * 7);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);

        const counts: Record<RetentionCategory, number> = {
          engajado: 0, oscilando: 0, em_risco: 0, abandono: 0, recuperado: 0,
        };

        for (const sid of studentIds) {
          const studentSessions = (sessionsByStudent[sid] ?? []).filter(
            s => new Date(s.completed_at).getTime() <= weekEnd.getTime()
          );
          const cat = classifyStudent(studentSessions, weekEnd);
          counts[cat]++;
        }

        trends.push({
          weekStart: weekStart.toISOString().slice(0, 10),
          counts,
        });
      }

      return trends;
    },
    enabled: !!personalId,
    staleTime: 5 * 60 * 1000,
  });
}
